import { motion } from "framer-motion";
import { Bot, Network, Swords, Globe2, FileBarChart } from "lucide-react";

const FEATURES = [
  {
    icon: Bot,
    title: "Real-time Bot & Click Farm Detection",
    desc: "Behavioral ML fingerprints every visitor and flags automated traffic, click farms and emulators in milliseconds.",
    span: "md:col-span-7",
    accent: "cyan",
  },
  {
    icon: Network,
    title: "Automated IP Blocking",
    desc: "Direct API sync with Google & Meta Ads pushes malicious IPs to your exclusion lists — no manual work.",
    span: "md:col-span-5",
    accent: "green",
  },
  {
    icon: Swords,
    title: "Competitor Click Prevention",
    desc: "Detect and neutralize rivals burning your budget with repeat malicious clicks.",
    span: "md:col-span-5",
    accent: "green",
  },
  {
    icon: Globe2,
    title: "Geo-Location & VPN / Proxy Filtering",
    desc: "Block traffic from data centers, proxies, VPNs and geographies outside your target market instantly.",
    span: "md:col-span-4",
    accent: "cyan",
  },
  {
    icon: FileBarChart,
    title: "Fraud Analytics & Chargeback Reports",
    desc: "Court-ready, exportable evidence for refund claims and detailed fraud dashboards.",
    span: "md:col-span-3",
    accent: "cyan",
  },
];

export const Features = () => {
  return (
    <section id="features" className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mb-16">
          <span className="font-mono text-xs tracking-widest uppercase text-cyan-neon">[02] Capabilities</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white mt-4 leading-tight">
            A full-stack fraud shield, engineered for paid media.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                whileHover={{ scale: 1.015 }}
                className={`${f.span} group relative rounded-2xl bg-[#0B101A] border border-white/8 p-7 lg:p-8 overflow-hidden hover:border-cyan-neon/30 transition-colors`}
              >
                <div
                  className={`absolute -right-16 -top-16 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                    f.accent === "green" ? "bg-[#39FF14]/15" : "bg-cyan-neon/15"
                  }`}
                />
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 border ${
                    f.accent === "green"
                      ? "bg-[#39FF14]/10 border-[#39FF14]/25"
                      : "bg-cyan-neon/10 border-cyan-neon/25"
                  }`}
                >
                  <Icon className={`w-6 h-6 ${f.accent === "green" ? "text-[#39FF14]" : "text-cyan-neon"}`} strokeWidth={1.75} />
                </div>
                <h3 className="font-display text-xl text-white mb-2.5">{f.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed font-light">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
